import { Card, CardContent, Typography, Skeleton, Box, useTheme } from '@mui/material';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { formatNumber } from './FormatValue';

export interface KpiCardProps {
  displayName: string;
  current: number;
  comparison?: number | null;
  delta?: number | null;
  unitType?: string;
  unitLabel?: string;
  decimals?: number;
  compact?: boolean;
  loading?: boolean;
}

export function KpiCard({
  displayName,
  current,
  delta,
  unitType,
  unitLabel,
  decimals,
  compact,
  loading,
}: KpiCardProps) {
  if (loading) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Skeleton width="60%" height={24} />
          <Skeleton width="40%" height={40} sx={{ mt: 1 }} />
          <Skeleton width="30%" height={20} sx={{ mt: 0.5 }} />
        </CardContent>
      </Card>
    );
  }

  const theme = useTheme();

  const formattedValue = formatNumber(
    current,
    unitType,
    unitType === 'currency' ? unitLabel : undefined,
    unitType !== 'currency' ? unitLabel : undefined,
    decimals,
    compact,
  );

  const deltaColor =
    delta != null ? (delta > 0 ? 'success.main' : delta < 0 ? 'error.main' : 'text.secondary') : undefined;

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="body2" color="text.secondary" noWrap>
          {displayName}
        </Typography>
        <Typography variant="h4" sx={{ mt: 1, fontWeight: 600 }}>
          {formattedValue}
        </Typography>
        {delta != null && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
            {delta > 0 ? (
              <TrendingUp size={16} color={theme.palette.success.main} />
            ) : delta < 0 ? (
              <TrendingDown size={16} color={theme.palette.error.main} />
            ) : null}
            <Typography variant="body2" color={deltaColor} component="span">
              {delta > 0 ? '+' : ''}
              {delta.toFixed(1)}%
            </Typography>
            <Typography variant="caption" color="text.disabled" component="span">
              vs prev period
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
